// @ts-check

/**
 * @file Renders a paginated async user select.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"

import ModelSelect from "../model-select"
import {User} from "../../frontend-models/user"
import {MODEL_SELECT_PAGE_SIZE, modelSelectOptionsResult, normalizedSelectValues} from "../model-select-options"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/**
 * @typedef {object} UsersSelectProps
 * @property {boolean} [closeOnChange] Close the dropdown after each selection change.
 * @property {string | number | null} [defaultValue] Initial selected user id.
 * @property {(string | number)[]} [defaultValues] Initial selected user ids.
 * @property {(string | number)[]} [excludeValues] User ids excluded from normal option pages.
 * @property {boolean} [includeEmailInLabel] Append email to labels when a name exists.
 * @property {string} [label] Select label.
 * @property {boolean} [multiple] Allow multiple selections.
 * @property {string} [name] Api Maker form field name.
 * @property {(...args: unknown[]) => unknown} [onChangeValue] Selection change handler.
 * @property {string} [placeholder] Placeholder text.
 * @property {string} testID Stable test id.
 * @property {(string | number)[]} [values] Controlled selected values.
 */

/** @typedef {Record<string, never>} UsersSelectState */

/**
 * User select that searches and paginates user labels server-side.
 * @augments {ShapeComponent<UsersSelectProps, UsersSelectState>}
 */
class UsersSelect extends ShapeComponent {
  static propTypes = propTypesExact({
    closeOnChange: PropTypes.bool,
    defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    defaultValues: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
    ),
    excludeValues: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
    ),
    includeEmailInLabel: PropTypes.bool,
    label: PropTypes.string,
    multiple: PropTypes.bool,
    name: PropTypes.string,
    onChangeValue: PropTypes.func,
    placeholder: PropTypes.string,
    testID: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
    )
  })
  static defaultProps = {
    closeOnChange: false,
    defaultValue: undefined,
    defaultValues: undefined,
    excludeValues: undefined,
    includeEmailInLabel: false,
    label: undefined,
    multiple: false,
    name: undefined,
    onChangeValue: undefined,
    placeholder: undefined,
    values: undefined
  }

  /** Initializes locale support. */
  setup() {
    useLocale()
  }

  /**
   * Renders the select.
   * @returns {ReactNode} User select.
   */
  render() {
    const {
      closeOnChange,
      defaultValue,
      defaultValues,
      label,
      multiple,
      name,
      onChangeValue,
      placeholder,
      testID,
      values
    } = this.p
    return (
      <ModelSelect
        closeOnChange={closeOnChange}
        defaultValue={defaultValue}
        defaultValues={defaultValues}
        label={label}
        multiple={multiple}
        name={name}
        onChangeValue={onChangeValue}
        options={this.tt.loadOptions}
        placeholder={placeholder || _("Search users...")}
        testID={testID}
        values={values}
      />
    )
  }

  /**
   * Loads user options for HayaSelect.
   * @param {{page?: number, searchValue?: string, values?: (string | number)[] | string | number}} args Loader args.
   * @returns {Promise<{options: {text: string, value: string}[], page: number, pageSize: number, totalCount: number}>} Paginated options.
   */
  loadOptions = async ({page = 1, searchValue, values} = {}) => {
    const selectedValues = normalizedSelectValues(values)
    if (selectedValues.length > 0) {
      const users = await User
        .where({id: selectedValues})
        .select(["id", "firstName", "lastName", "email"])
        .order([["firstName", "asc"], ["lastName", "asc"], ["email", "asc"], ["id", "asc"]])
        .toArray()
      return modelSelectOptionsResult({
        options: users.map((user) => this.userToOption(user)),
        page: 1,
        totalCount: users.length
      })
    }

    const excludedValues = normalizedSelectValues(this.p.excludeValues)
    if (searchValue) {
      return await this.loadSearchOptions({excludedValues, page, searchValue})
    }
    /** @type {Record<string, unknown>} */
    const ransackParams = {}
    if (excludedValues.length > 0) ransackParams.idNotIn = excludedValues
    const baseQuery = User.ransack(ransackParams)
    const [users, totalCount] = await Promise.all([
      baseQuery
        .clone()
        .select(["id", "firstName", "lastName", "email"])
        .order([["firstName", "asc"], ["lastName", "asc"], ["email", "asc"], ["id", "asc"]])
        .page(page)
        .perPage(MODEL_SELECT_PAGE_SIZE)
        .toArray(),
      baseQuery.clone().count()
    ])
    return modelSelectOptionsResult({
      options: users.map((user) => this.userToOption(user)),
      page,
      totalCount
    })
  }

  /**
   * Searches user options across name and email columns without relying on unsupported compound ransack keys.
   * @param {object} args Pagination, filtering, and exclusion inputs from HayaSelect.
   * @param {(string | number)[]} args.excludedValues User ids excluded from results.
   * @param {number} args.page Requested result page.
   * @param {string} args.searchValue Text typed by the user.
   * @returns {Promise<{options: {text: string, value: string}[], page: number, pageSize: number, totalCount: number}>} Paginated options.
   */
  async loadSearchOptions({excludedValues, page, searchValue}) {
    const normalizedPage = Math.max(page || 1, 1)
    const searchSpecs = this.searchSpecs(searchValue)
    const idLists = await Promise.all(
      searchSpecs.map((ransackParams) => User
        .ransack({...ransackParams, ...this.excludedRansackParams(excludedValues)})
        .pluck("id"))
    )
    const matchingIds = Array.from(new Set(idLists.flat().map((id) => String(id))))

    if (matchingIds.length === 0) {
      return modelSelectOptionsResult({
        options: [],
        page: normalizedPage,
        totalCount: 0
      })
    }

    const users = await User
      .where({id: matchingIds})
      .select(["id", "firstName", "lastName", "email"])
      .order([["firstName", "asc"], ["lastName", "asc"], ["email", "asc"], ["id", "asc"]])
      .page(normalizedPage)
      .perPage(MODEL_SELECT_PAGE_SIZE)
      .toArray()

    return modelSelectOptionsResult({
      options: users.map((user) => this.userToOption(user)),
      page: normalizedPage,
      totalCount: matchingIds.length
    })
  }

  /**
   * Builds one-field ransack specs for each search token.
   * @param {string} searchValue Text typed by the user.
   * @returns {Record<string, unknown>[]} Ransack hashes for supported user columns.
   */
  searchSpecs(searchValue) {
    const tokens = searchValue.trim().split(/\s+/).filter(Boolean)
    const columns = ["firstName", "lastName", "email"]

    return tokens.flatMap((token) => columns.map((column) => ({
      [`${column}Cont`]: token
    })))
  }

  /**
   * Builds a ransack exclusion hash for ids hidden from normal option pages.
   * @param {(string | number)[]} excludedValues User ids to exclude.
   * @returns {Record<string, unknown>} Empty hash or id exclusion predicate.
   */
  excludedRansackParams(excludedValues) {
    return excludedValues.length > 0
      ? {idNotIn: excludedValues}
      : {}
  }

  /**
   * Converts a user model into a select option.
   * @param {InstanceType<typeof User>} user User model.
   * @returns {{text: string, value: string}} Text/value pair using name and email fields.
   */
  userToOption(user) {
    return {
      text: this.userLabel(user),
      value: String(user.id())
    }
  }

  /**
   * Builds the visible user label.
   * @param {InstanceType<typeof User>} user User model.
   * @returns {string} User name, email, or id fallback.
   */
  userLabel(user) {
    const firstName = (user.firstName() || "").trim()
    const lastName = (user.lastName() || "").trim()
    const email = (user.email() || "").trim()
    const name = `${firstName} ${lastName}`.trim()

    return this.p.includeEmailInLabel && name && email
      ? `${name} (${email})`
      : name || email || String(user.id())
  }
}

/** @type {NamedExoticComponent<UsersSelectProps>} */
const UsersSelectComponent = memo(shapeComponent(UsersSelect))

export default UsersSelectComponent
